import type { ReactNode } from 'react'
import { createRoute, type AnyRootRoute } from '@tanstack/react-router'
import { ReplayRoute } from './features/replay/ReplayRoute'
import { SharedReplayRoute } from './features/replay/SharedReplayRoute'
import { LinkRoute } from './features/link/LinkRoute'
import { campfirePaths } from './paths'

/** What the shell configures about campfire's routes. */
export interface CampfireRouteOptions {
  /**
   * The link service domains a share link code is looked up on, in order. Empty
   * forwards every link to where the link service has always sent browsers.
   */
  shareLinkDomains?: readonly string[]
  /**
   * The way onto a team's own map from a shared pin or line, for a signed-in
   * member of the team. The shell knows both clubhouse's routes and the viewer's
   * teams; campfire knows neither, so it only offers the slot.
   */
  renderTeamLink?: (groupId: string) => ReactNode
}

export function buildCampfireRoutes<TRoot extends AnyRootRoute>(
  rootRoute: TRoot,
  { shareLinkDomains = [], renderTeamLink }: CampfireRouteOptions = {},
) {
  const huntReplayRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: campfirePaths.huntReplay,
    component: ReplayRoute,
    // The route reads these itself off the location; validating here keeps an
    // unexpected value from reaching it as anything but a string.
    validateSearch: (search: Record<string, unknown>) => ({
      from: typeof search.from === 'string' ? search.from : undefined,
      teamName: typeof search.teamName === 'string' ? search.teamName : undefined,
      source: typeof search.source === 'string' ? search.source : undefined,
    }),
  })
  // The shared page is the only landing of a link that was already sent, so it
  // is registered whatever the flag says.
  const sharedReplayRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: campfirePaths.sharedReplay,
    component: SharedReplayRoute,
  })
  const linkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: campfirePaths.link,
    component: () => <LinkRoute shareLinkDomains={shareLinkDomains} renderTeamLink={renderTeamLink} />,
  })
  return [huntReplayRoute, sharedReplayRoute, linkRoute]
}
